import { useState } from 'react';
import type { Report, RightsStatus, Role, Track, Video } from '@blackblack/shared';
import { can, formatCount, ROLES } from '@blackblack/shared';
import { api, errorMessage } from '../../lib/api';
import { useInfinite, useQuery } from '../../lib/query';
import { goBack, Link, navigate } from '../../lib/router';
import { useAuth } from '../../state/auth';
import { useToast } from '../../state/toast';
import { Icon } from '../../components/ui/Icon';
import { Avatar, Button, Chips, EmptyState, ErrorState, IconButton, LoadMore, Skeleton, Spinner, cx } from '../../components/ui/primitives';
import { Wordmark } from '../../components/ui/Logo';
import { timeAgo } from '../../lib/time';
import styles from './admin.module.css';

type Section = 'overview' | 'reports' | 'users' | 'videos' | 'music' | 'audit';

const SECTIONS: Array<{ id: Section; label: string; icon: 'chart' | 'flag' | 'users' | 'grid' | 'music' | 'shield'; perm: Parameters<typeof can>[1] }> = [
  { id: 'overview', label: 'Overzicht', icon: 'chart', perm: 'stats.view' },
  { id: 'reports', label: 'Meldingen', icon: 'flag', perm: 'reports.view' },
  { id: 'users', label: 'Gebruikers', icon: 'users', perm: 'users.view' },
  { id: 'videos', label: "Video's", icon: 'grid', perm: 'reports.view' },
  { id: 'music', label: 'Muziek', icon: 'music', perm: 'music.manage' },
  { id: 'audit', label: 'Audit log', icon: 'shield', perm: 'audit.view' },
];

/** Trust & safety / admin console. Every section is permission-gated server-side as well. */
export function AdminPage() {
  const { me } = useAuth();
  const allowed = SECTIONS.filter((s) => me && can(me.role, s.perm));
  const [section, setSection] = useState<Section>(allowed[0]?.id ?? 'reports');

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <IconButton icon="back" label="Terug" onClick={() => goBack('/settings')} />
        <div className={styles.title}>
          <Wordmark size={18} />
          <span className={styles.badge}>Admin</span>
        </div>
        <span className={styles.role}>{me?.role}</span>
      </header>
      <nav className={styles.nav}>
        {allowed.map((s) => (
          <button key={s.id} className={cx(styles.navItem, section === s.id && styles.navActive)} onClick={() => setSection(s.id)}>
            <Icon name={s.icon} size={18} /> {s.label}
          </button>
        ))}
      </nav>
      <main className={styles.main}>
        {section === 'overview' && <Overview />}
        {section === 'reports' && <Reports />}
        {section === 'users' && <Users />}
        {section === 'videos' && <Videos />}
        {section === 'music' && <Music />}
        {section === 'audit' && <Audit />}
      </main>
    </div>
  );
}

/* ---------------------------------------------------------------- overview */

interface Stats {
  totals: Record<'users' | 'videos' | 'tracks' | 'playlists' | 'openReports' | 'suspendedUsers', number>;
  period: { days: number; newUsers: number; newVideos: number; views: number; watchTimeSec: number; completionRate: number; songPlays: number; playlistPlays: number; searches: number; shares: number; likes: number; follows: number };
  daily: Array<{ day: string; views: number; plays: number }>;
}

function Overview() {
  const [days, setDays] = useState<'7' | '30' | '90'>('30');
  const q = useQuery<Stats>(`admin:stats:${days}`, () => api.get('/admin/stats', { days }), { staleMs: 10_000 });
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const s = q.data;
  const max = Math.max(1, ...(s?.daily.map((d) => d.views + d.plays) ?? [1]));
  const kpi = (label: string, value: string | number | undefined, hint?: string) => (
    <div className={styles.kpi}>
      <span>{label}</span>
      <strong>{value === undefined ? <Skeleton w={60} h={24} /> : typeof value === 'number' ? formatCount(value) : value}</strong>
      {hint && <small>{hint}</small>}
    </div>
  );
  return (
    <>
      <div className={styles.toolbar}>
        <h2>Platform</h2>
        <Chips items={[{ id: '7', label: '7 dagen' }, { id: '30', label: '30 dagen' }, { id: '90', label: '90 dagen' }]} value={days} onChange={setDays} />
      </div>
      <div className={styles.kpis}>
        {kpi('Gebruikers', s?.totals.users, s && `+${s.period.newUsers} nieuw`)}
        {kpi("Video's", s?.totals.videos, s && `+${s.period.newVideos} nieuw`)}
        {kpi('Nummers', s?.totals.tracks)}
        {kpi('Playlists', s?.totals.playlists)}
        {kpi('Open meldingen', s?.totals.openReports)}
        {kpi('Geschorst', s?.totals.suspendedUsers)}
      </div>
      <h2 className={styles.h2}>Engagement ({days} dagen)</h2>
      <div className={styles.kpis}>
        {kpi('Views', s?.period.views)}
        {kpi('Kijktijd', s ? `${Math.round(s.period.watchTimeSec / 60)} min` : undefined)}
        {kpi('Completion rate', s ? `${s.period.completionRate}%` : undefined)}
        {kpi('Likes', s?.period.likes)}
        {kpi('Shares', s?.period.shares)}
        {kpi('Follows', s?.period.follows)}
        {kpi('Song plays', s?.period.songPlays)}
        {kpi('Playlist plays', s?.period.playlistPlays)}
        {kpi('Zoekopdrachten', s?.period.searches)}
      </div>
      <h2 className={styles.h2}>Activiteit per dag</h2>
      <div className={styles.chart} role="img" aria-label="Views en song plays per dag">
        {s?.daily.length === 0 && <p className={styles.muted}>Nog geen events in deze periode.</p>}
        {s?.daily.map((d) => (
          <div key={d.day} className={styles.bar} title={`${d.day}: ${d.views} views, ${d.plays} plays`}>
            <span className={styles.barPlays} style={{ height: `${(d.plays / max) * 100}%` }} />
            <span className={styles.barViews} style={{ height: `${(d.views / max) * 100}%` }} />
            <small>{d.day.slice(8)}</small>
          </div>
        ))}
      </div>
      <p className={styles.legend}>
        <i className={styles.barViews} /> Video views <i className={styles.barPlays} /> Song plays
      </p>
    </>
  );
}

/* ---------------------------------------------------------------- reports */

function Reports() {
  const [status, setStatus] = useState<'open' | 'actioned' | 'dismissed'>('open');
  const list = useInfinite<Report>(`admin:reports:${status}`, (cursor) => api.page<Report>('/admin/reports', { status, cursor }));
  const toast = useToast();
  const { me } = useAuth();
  const [busy, setBusy] = useState<string | null>(null);

  const resolve = async (r: Report, action: 'dismiss' | 'remove_content' | 'suspend_user') => {
    const note = action === 'dismiss' ? undefined : prompt('Notitie (optioneel)') ?? undefined;
    setBusy(r.id);
    try {
      await api.post(`/admin/reports/${r.id}/resolve`, { action, note });
      toast('Melding afgehandeld', { tone: 'success' });
      await list.refresh();
    } catch (e) {
      toast(errorMessage(e), { tone: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const REASON: Record<string, string> = { spam: 'Spam', harassment: 'Intimidatie', hate: 'Haat', nudity: 'Naaktheid', violence: 'Geweld', copyright: 'Auteursrecht', self_harm: 'Zelfbeschadiging', misinformation: 'Desinformatie', other: 'Overig' };

  return (
    <>
      <div className={styles.toolbar}>
        <h2>Meldingen</h2>
        <Chips items={[{ id: 'open', label: 'Open' }, { id: 'actioned', label: 'Actie genomen' }, { id: 'dismissed', label: 'Afgewezen' }]} value={status} onChange={setStatus} />
      </div>
      {list.initialLoading && <Spinner />}
      {!list.initialLoading && list.items.length === 0 && <EmptyState icon="check" title="Geen meldingen" text="Alles is afgehandeld." />}
      <ul className={styles.cards}>
        {list.items.map((r) => (
          <li key={r.id} className={styles.card}>
            <div className={styles.cardTop}>
              {r.target?.thumbnailUrl && <img src={r.target.thumbnailUrl} alt="" className={styles.thumb} />}
              <div className={styles.cardText}>
                <span className={styles.pill}>{r.targetType}</span> <span className={cx(styles.pill, styles.pillWarn)}>{REASON[r.reason] ?? r.reason}</span>
                <p className={styles.target}>{r.target?.label ?? '(verwijderd)'}</p>
                {r.details && <p className={styles.muted}>“{r.details}”</p>}
                <p className={styles.muted}>
                  door @{r.reporter.username} · {timeAgo(r.createdAt)}
                </p>
              </div>
            </div>
            {r.status === 'open' && me && can(me.role, 'reports.resolve') && (
              <div className={styles.cardActions}>
                {r.targetType === 'video' && (
                  <Button size="sm" variant="ghost" icon="eye" onClick={() => navigate(`/v/${r.targetId}`)}>
                    Bekijken
                  </Button>
                )}
                <Button size="sm" variant="secondary" disabled={busy === r.id} onClick={() => resolve(r, 'dismiss')}>
                  Afwijzen
                </Button>
                {r.targetType !== 'user' && (
                  <Button size="sm" variant="danger" disabled={busy === r.id} onClick={() => resolve(r, 'remove_content')}>
                    Verwijderen
                  </Button>
                )}
                {can(me.role, 'users.suspend') && (
                  <Button size="sm" variant="danger" disabled={busy === r.id} onClick={() => resolve(r, 'suspend_user')}>
                    Schors 7 dagen
                  </Button>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
      <LoadMore onVisible={list.loadMore} loading={list.loading} hasMore={list.hasMore} />
    </>
  );
}

/* ---------------------------------------------------------------- users */

interface AdminUser {
  id: string;
  username: string;
  displayName: string;
  email: string;
  avatarUrl: string | null;
  role: Role;
  status: 'active' | 'suspended' | 'deleted';
  suspendedUntil: string | null;
  followerCount: number;
  videoCount: number;
  createdAt: string;
}

function Users() {
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const list = useInfinite<AdminUser>(`admin:users:${search}`, (cursor) => api.page<AdminUser>('/admin/users', { q: search, cursor }));
  const toast = useToast();
  const { me } = useAuth();

  const act = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn();
      toast(msg, { tone: 'success' });
      await list.refresh();
    } catch (e) {
      toast(errorMessage(e), { tone: 'error' });
    }
  };

  return (
    <>
      <div className={styles.toolbar}>
        <h2>Gebruikers</h2>
        <form
          className={styles.search}
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(q.trim());
          }}
        >
          <Icon name="search" size={16} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Naam, @username of e-mail" />
        </form>
      </div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Gebruiker</th>
              <th>Rol</th>
              <th>Status</th>
              <th>Video's</th>
              <th>Lid sinds</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {list.items.map((u) => (
              <tr key={u.id}>
                <td>
                  <Link to={`/u/${u.username}`} className={styles.userCell}>
                    <Avatar user={u} size={32} />
                    <span>
                      <strong>@{u.username}</strong>
                      <small>{u.email}</small>
                    </span>
                  </Link>
                </td>
                <td>
                  {me && can(me.role, 'users.setRole') && u.id !== me.id ? (
                    <select value={u.role} onChange={(e) => void act(() => api.patch(`/admin/users/${u.id}/role`, { role: e.target.value }), 'Rol aangepast')} className={styles.select}>
                      {ROLES.map((r) => (
                        <option key={r}>{r}</option>
                      ))}
                    </select>
                  ) : (
                    u.role
                  )}
                </td>
                <td>
                  <span className={cx(styles.pill, u.status === 'suspended' && styles.pillDanger)}>{u.status === 'suspended' ? 'geschorst' : 'actief'}</span>
                </td>
                <td>{u.videoCount}</td>
                <td>{new Date(u.createdAt).toLocaleDateString('nl-NL')}</td>
                <td>
                  {me && can(me.role, 'users.suspend') && u.id !== me.id &&
                    (u.status === 'suspended' ? (
                      <Button size="sm" variant="secondary" onClick={() => void act(() => api.post(`/admin/users/${u.id}/unsuspend`), 'Schorsing opgeheven')}>
                        Opheffen
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() => {
                          const reason = prompt(`Reden om @${u.username} te schorsen?`);
                          if (reason && reason.length >= 3) void act(() => api.post(`/admin/users/${u.id}/suspend`, { reason, days: 7 }), 'Gebruiker geschorst');
                        }}
                      >
                        Schorsen
                      </Button>
                    ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <LoadMore onVisible={list.loadMore} loading={list.loading} hasMore={list.hasMore} />
    </>
  );
}

/* ---------------------------------------------------------------- videos */

function Videos() {
  const [status, setStatus] = useState<'all' | 'published' | 'under_review' | 'removed'>('all');
  const list = useInfinite<Video & { status: string }>(`admin:videos:${status}`, (cursor) => api.page('/admin/videos', { status: status === 'all' ? undefined : status, cursor }));
  const toast = useToast();
  const act = async (fn: () => Promise<unknown>, msg: string) => {
    try {
      await fn();
      toast(msg, { tone: 'success' });
      await list.refresh();
    } catch (e) {
      toast(errorMessage(e), { tone: 'error' });
    }
  };
  return (
    <>
      <div className={styles.toolbar}>
        <h2>Video's</h2>
        <Chips items={[{ id: 'all', label: 'Alle' }, { id: 'published', label: 'Gepubliceerd' }, { id: 'under_review', label: 'Ter beoordeling' }, { id: 'removed', label: 'Verwijderd' }]} value={status} onChange={setStatus} />
      </div>
      <div className={styles.videoGrid}>
        {list.items.map((v) => (
          <div key={v.id} className={styles.videoCard}>
            <button onClick={() => navigate(`/v/${v.id}`)} className={styles.videoThumb}>
              {v.thumbnailUrl && <img src={v.thumbnailUrl} alt="" loading="lazy" />}
              <span className={cx(styles.pill, styles.videoStatus, v.status === 'removed' && styles.pillDanger, v.status === 'under_review' && styles.pillWarn)}>{v.status}</span>
            </button>
            <p className={styles.videoCaption}>@{v.author.username} · {v.caption.slice(0, 60)}</p>
            <div className={styles.videoActions}>
              {v.status === 'under_review' && (
                <Button size="sm" variant="secondary" onClick={() => void act(() => api.post(`/admin/videos/${v.id}/approve`), 'Goedgekeurd')}>
                  Goedkeuren
                </Button>
              )}
              {v.status === 'removed' ? (
                <Button size="sm" variant="secondary" onClick={() => void act(() => api.post(`/admin/videos/${v.id}/restore`), 'Hersteld')}>
                  Herstellen
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => {
                    const reason = prompt('Reden voor verwijderen?');
                    if (reason && reason.length >= 3) void act(() => api.post(`/admin/videos/${v.id}/remove`, { reason }), 'Video verwijderd');
                  }}
                >
                  Verwijderen
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
      {!list.initialLoading && list.items.length === 0 && <EmptyState icon="grid" title="Geen video's" />}
      <LoadMore onVisible={list.loadMore} loading={list.loading} hasMore={list.hasMore} />
    </>
  );
}

/* ---------------------------------------------------------------- music */

function Music() {
  const q = useQuery<Array<Track & { rightsStatus: RightsStatus }>>('admin:music', () => api.get('/admin/music', { limit: 200 }), { staleMs: 0 });
  const toast = useToast();
  const update = async (id: string, patch: Record<string, unknown>) => {
    try {
      await api.patch(`/admin/music/${id}/rights`, patch);
      toast('Rechten bijgewerkt', { tone: 'success' });
      await q.refetch();
    } catch (e) {
      toast(errorMessage(e), { tone: 'error' });
    }
  };
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  return (
    <>
      <div className={styles.toolbar}>
        <h2>Muziekcatalogus & rechten</h2>
      </div>
      <p className={styles.muted}>Rechtenstatus bepaalt of een nummer volledig, als preview of niet speelt en of het in video's mag worden gebruikt. Takedowns zijn direct actief.</p>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Nummer</th>
              <th>Bron</th>
              <th>Rechten</th>
              <th>Volledig</th>
              <th>In video's</th>
              <th>Gebruik</th>
            </tr>
          </thead>
          <tbody>
            {q.loading && (
              <tr>
                <td colSpan={6}>
                  <Spinner />
                </td>
              </tr>
            )}
            {q.data?.map((t) => (
              <tr key={t.id}>
                <td>
                  <Link to={`/sound/${t.id}`} className={styles.userCell}>
                    {t.coverUrl ? <img src={t.coverUrl} alt="" className={styles.miniCover} /> : <span className={styles.miniCover} />}
                    <span>
                      <strong>{t.title}</strong>
                      <small>{t.artist.name}</small>
                    </span>
                  </Link>
                </td>
                <td>
                  <span className={styles.pill}>{t.sourceType}</span>
                </td>
                <td>
                  <select className={styles.select} value={t.rightsStatus} onChange={(e) => void update(t.id, { rightsStatus: e.target.value })}>
                    {(['cleared', 'pending', 'restricted', 'taken_down'] as const).map((s) => (
                      <option key={s}>{s}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <input type="checkbox" checked={t.fullPlayback} onChange={(e) => void update(t.id, { fullPlayback: e.target.checked })} aria-label="Volledig afspelen" />
                </td>
                <td>
                  <input type="checkbox" checked={t.usableInVideos} onChange={(e) => void update(t.id, { usableInVideos: e.target.checked })} aria-label="Bruikbaar in video's" />
                </td>
                <td>
                  {t.videoCount} video's · {formatCount(t.playCount)} plays
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/* ---------------------------------------------------------------- audit */

interface AuditEntry {
  id: string;
  actor: { id: string; username: string } | null;
  action: string;
  targetType: string | null;
  targetId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

function Audit() {
  const list = useInfinite<AuditEntry>('admin:audit', (cursor) => api.page<AuditEntry>('/admin/audit', { cursor }));
  return (
    <>
      <div className={styles.toolbar}>
        <h2>Audit log</h2>
        <IconButton icon="refresh" label="Vernieuwen" onClick={() => void list.refresh()} />
      </div>
      {!list.initialLoading && list.items.length === 0 && <EmptyState icon="shield" title="Nog geen acties gelogd" />}
      <ul className={styles.audit}>
        {list.items.map((a) => (
          <li key={a.id}>
            <code>{a.action}</code>
            <span>
              {a.actor ? `@${a.actor.username}` : 'systeem'} → {a.targetType} <small>{a.targetId}</small>
            </span>
            {a.metadata && Object.keys(a.metadata).length > 0 && <small className={styles.muted}>{JSON.stringify(a.metadata)}</small>}
            <time>{timeAgo(a.createdAt)}</time>
          </li>
        ))}
      </ul>
      <LoadMore onVisible={list.loadMore} loading={list.loading} hasMore={list.hasMore} />
    </>
  );
}
